import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { ArrowRight } from 'lucide-react';

const HERO_IMAGES = [
  '/images/hero/background.jpg',
  '/images/gallery/image-1.jpg',
  '/images/gallery/image-3.jpg',
  '/images/gallery/image-7.jpg',
  '/images/gallery/image-5.jpg',
];

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % HERO_IMAGES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Slideshow with Overlay */}
      <div className="absolute inset-0 z-0">
        {HERO_IMAGES.map((src, i) => (
          <div key={src} className={`hero-slide ${i === current ? 'active' : ''}`} aria-hidden={i !== current}>
            <img src={src} alt="Corbaci" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/90"></div>
        <div className="absolute inset-0 bg-black/10"></div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid="hero-slide-dots">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-[#d4af37]' : 'w-2 bg-white/40 hover:bg-white/70'}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        <div className="space-y-6 animate-fade-in">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: "'Crimson Text', serif", textShadow: '0 2px 20px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.9)' }}>
            {t.hero_title}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8" style={{ textShadow: '0 2px 16px rgba(0,0,0,0.85)' }}>
            {t.hero_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="/bestellung"
              className="group bg-[#d4af37] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#f0c24d] transition-all duration-300 transform hover:scale-105 flex items-center gap-2 shadow-lg"
            >
              {t.order_now}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
            </a>
            <a 
              href="/speisekarte"
              className="group bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 flex items-center gap-2 shadow-lg"
            >
              {t.view_menu}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="hidden sm:block absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#d4af37] rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#d4af37] rounded-full animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
